import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FiPlus, FiEdit2, FiTrash2, FiMapPin, FiDownload, FiCalendar, FiX, FiRefreshCw, FiInfo, FiUsers, FiLayers, FiArrowRight } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../config/api'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { useAuth } from '../contexts/AuthContext'
import { PageHeader, Btn } from '../ui'
import { formatearFecha } from '../utils/fechas'

const COMUNIDADES = [
  { codigo: 'ES-AN', nombre: 'Andalucía' }, { codigo: 'ES-AR', nombre: 'Aragón' },
  { codigo: 'ES-AS', nombre: 'Asturias' }, { codigo: 'ES-IB', nombre: 'Illes Balears' },
  { codigo: 'ES-CN', nombre: 'Canarias' }, { codigo: 'ES-CB', nombre: 'Cantabria' },
  { codigo: 'ES-CL', nombre: 'Castilla y León' }, { codigo: 'ES-CM', nombre: 'Castilla-La Mancha' },
  { codigo: 'ES-CT', nombre: 'Catalunya' }, { codigo: 'ES-EX', nombre: 'Extremadura' },
  { codigo: 'ES-GA', nombre: 'Galicia' }, { codigo: 'ES-MD', nombre: 'Comunidad de Madrid' },
  { codigo: 'ES-MC', nombre: 'Región de Murcia' }, { codigo: 'ES-NC', nombre: 'Navarra' },
  { codigo: 'ES-PV', nombre: 'País Vasco' }, { codigo: 'ES-RI', nombre: 'La Rioja' },
  { codigo: 'ES-VC', nombre: 'Comunitat Valenciana' }, { codigo: 'ES-CE', nombre: 'Ceuta' },
  { codigo: 'ES-ML', nombre: 'Melilla' }
]

const TIPO_FESTIVO_LABEL = { nacional: 'Nacional', autonomico: 'Autonómico', local: 'Local', empresa: 'Empresa' }
const TIPO_FESTIVO_COLOR = { nacional: 'bg-blue-100 text-blue-700', autonomico: 'bg-purple-100 text-purple-700', local: 'bg-green-100 text-green-700', empresa: 'bg-orange-100 text-orange-700' }

export default function GestionCentrosTrabajo() {
  const { empleado } = useAuth()
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()

  const [centros, setCentros] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalCentro, setModalCentro] = useState(false)
  const [centroEditar, setCentroEditar] = useState(null)
  const [centroSeleccionado, setCentroSeleccionado] = useState(null)
  const [festivos, setFestivos] = useState([])
  const [anioFestivos, setAnioFestivos] = useState(new Date().getFullYear())
  const [loadingFestivos, setLoadingFestivos] = useState(false)
  const [modalFestivo, setModalFestivo] = useState(false)
  const [festivoEditar, setFestivoEditar] = useState(null)
  const [importando, setImportando] = useState(false)
  const [anioImportar, setAnioImportar] = useState(new Date().getFullYear())

  const [formCentro, setFormCentro] = useState({
    nombre: '', direccion: '', latitud: '', longitud: '', radio_metros: 200,
    pais: 'ES', comunidad_autonoma: '', municipio: ''
  })
  const [formFestivo, setFormFestivo] = useState({ fecha: '', nombre: '', tipo: 'empresa' })

  const buildUrl = (path) => {
    const sep = path.includes('?') ? '&' : '?'
    return esGestion && empresaGestionId
      ? `${API_URL}${path}${sep}empresa_id=${empresaGestionId}`
      : `${API_URL}${path}`
  }

  const token = () => localStorage.getItem('token')
  const headers = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' })

  useEffect(() => { cargarCentros() }, [empresaGestionId])

  const cargarCentros = async () => {
    setLoading(true)
    try {
      const r = await fetch(buildUrl('/centros-trabajo'), { headers: headers() })
      if (r.ok) setCentros(await r.json())
    } catch { toast.error('Error al cargar centros de trabajo') }
    finally { setLoading(false) }
  }

  const cargarFestivos = async (centroId, anio) => {
    setLoadingFestivos(true)
    try {
      const r = await fetch(buildUrl(`/centros-trabajo/${centroId}/festivos?anio=${anio}`), { headers: headers() })
      if (r.ok) setFestivos(await r.json())
    } catch { toast.error('Error al cargar festivos') }
    finally { setLoadingFestivos(false) }
  }

  const abrirModalCentro = (centro = null) => {
    setCentroEditar(centro)
    setFormCentro(centro ? {
      nombre: centro.nombre, direccion: centro.direccion || '',
      latitud: centro.latitud || '', longitud: centro.longitud || '',
      radio_metros: centro.radio_metros || 200, pais: centro.pais || 'ES',
      comunidad_autonoma: centro.comunidad_autonoma || '', municipio: centro.municipio || ''
    } : { nombre: '', direccion: '', latitud: '', longitud: '', radio_metros: 200, pais: 'ES', comunidad_autonoma: '', municipio: '' })
    setModalCentro(true)
  }

  const guardarCentro = async (e) => {
    e.preventDefault()
    const body = {
      ...formCentro,
      latitud: formCentro.latitud ? parseFloat(formCentro.latitud) : null,
      longitud: formCentro.longitud ? parseFloat(formCentro.longitud) : null,
      radio_metros: parseInt(formCentro.radio_metros) || 200
    }
    try {
      const url = centroEditar ? buildUrl(`/centros-trabajo/${centroEditar.id}`) : buildUrl('/centros-trabajo')
      const r = await fetch(url, { method: centroEditar ? 'PUT' : 'POST', headers: headers(), body: JSON.stringify(body) })
      if (r.ok) {
        toast.success(centroEditar ? 'Centro actualizado' : 'Centro creado')
        setModalCentro(false)
        cargarCentros()
        if (centroSeleccionado?.id === centroEditar?.id) {
          const updated = await r.json()
          setCentroSeleccionado(updated)
        }
      } else {
        const err = await r.json()
        toast.error(err.error || 'Error al guardar')
      }
    } catch { toast.error('Error de conexión') }
  }

  const eliminarCentro = async (id) => {
    if (!window.confirm('¿Eliminar este centro de trabajo?')) return
    try {
      const r = await fetch(buildUrl(`/centros-trabajo/${id}`), { method: 'DELETE', headers: headers() })
      if (r.ok) {
        toast.success('Centro eliminado')
        if (centroSeleccionado?.id === id) setCentroSeleccionado(null)
        cargarCentros()
      } else {
        const err = await r.json()
        toast.error(err.error || 'Error al eliminar')
      }
    } catch { toast.error('Error de conexión') }
  }

  const seleccionarCentro = (centro) => {
    setCentroSeleccionado(centro)
    setFestivos([])
    cargarFestivos(centro.id, anioFestivos)
  }

  const importarFestivos = async () => {
    if (!centroSeleccionado) return
    setImportando(true)
    try {
      const r = await fetch(buildUrl(`/centros-trabajo/${centroSeleccionado.id}/festivos/importar`), {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ anio: anioImportar, incluir_nacionales: true, incluir_autonomicos: true })
      })
      const data = await r.json()
      if (r.ok) {
        toast.success(`Importados ${data.insertados} festivos para ${data.anio} (${data.duplicados} ya existían)`)
        if (data.advertencias) data.advertencias.forEach(w => toast(w, { icon: '⚠️' }))
        cargarFestivos(centroSeleccionado.id, anioFestivos)
      } else {
        toast.error(data.error || 'Error al importar festivos')
      }
    } catch { toast.error('Error de conexión') }
    finally { setImportando(false) }
  }

  const guardarFestivo = async (e) => {
    e.preventDefault()
    try {
      const url = festivoEditar
        ? buildUrl(`/centros-trabajo/${centroSeleccionado.id}/festivos/${festivoEditar.id}`)
        : buildUrl(`/centros-trabajo/${centroSeleccionado.id}/festivos`)
      const r = await fetch(url, {
        method: festivoEditar ? 'PUT' : 'POST', headers: headers(),
        body: JSON.stringify(formFestivo)
      })
      if (r.ok) {
        toast.success(festivoEditar ? 'Festivo actualizado' : 'Festivo añadido')
        setModalFestivo(false)
        cargarFestivos(centroSeleccionado.id, anioFestivos)
      } else {
        const err = await r.json()
        toast.error(err.error || 'Error al guardar')
      }
    } catch { toast.error('Error de conexión') }
  }

  const eliminarFestivo = async (festivoId) => {
    if (!window.confirm('¿Eliminar este festivo?')) return
    try {
      const r = await fetch(buildUrl(`/centros-trabajo/${centroSeleccionado.id}/festivos/${festivoId}`), { method: 'DELETE', headers: headers() })
      if (r.ok) { toast.success('Festivo eliminado'); cargarFestivos(centroSeleccionado.id, anioFestivos) }
    } catch { toast.error('Error de conexión') }
  }

  const abrirModalFestivo = (festivo = null) => {
    setFestivoEditar(festivo)
    setFormFestivo(festivo ? { fecha: festivo.fecha, nombre: festivo.nombre, tipo: festivo.tipo } : { fecha: '', nombre: '', tipo: 'empresa' })
    setModalFestivo(true)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ N1"
        kicker="Centros"
        title="Centros de"
        italic="trabajo."
        sub="Gestiona los centros de trabajo y sus calendarios de festivos."
        actions={<Btn variant="primary" size="sm" onClick={() => abrirModalCentro()}><FiPlus size={14} /> Nuevo centro</Btn>}
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      {/* Leyenda: cómo llegan los festivos a los empleados (Punto 1) */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2.5">
        <FiInfo className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
        <div className="text-sm text-blue-900">
          Los festivos de un centro se aplican a los empleados <strong>a través de los grupos</strong>: un empleado
          hereda los festivos del centro asignado a su grupo. Un empleado sin grupo (o cuyo grupo no tiene centro)
          no tiene festivos asignados.
          <Link to="../grupos" className="inline-flex items-center gap-1 ml-1 font-medium text-blue-700 hover:underline">
            Gestionar grupos <FiArrowRight size={12} />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de centros */}
        <div className="lg:col-span-1">
          <div className="card p-4">
            <h2 className="font-semibold text-[var(--ink-2)] mb-3">Centros ({centros.length})</h2>
            {loading ? (
              <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-[var(--bg-2)] rounded animate-pulse" />)}</div>
            ) : centros.length === 0 ? (
              <p className="text-[var(--ink-4)] text-sm text-center py-8">No hay centros de trabajo</p>
            ) : (
              <div className="space-y-2">
                {centros.map(ct => (
                  <div
                    key={ct.id}
                    onClick={() => seleccionarCentro(ct)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${centroSeleccionado?.id === ct.id ? 'border-blue-500 bg-blue-50' : 'border-[var(--line)] hover:border-[var(--line-2)] hover:bg-[var(--bg-2)]'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[var(--ink)] truncate">{ct.nombre}</p>
                        {ct.direccion && <p className="text-xs text-[var(--ink-3)] truncate mt-0.5">{ct.direccion}</p>}
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-[var(--ink-4)]">
                          <span className="inline-flex items-center gap-1"><FiLayers size={10} /> {ct.num_grupos} grupo(s)</span>
                          <span className="inline-flex items-center gap-1"><FiUsers size={10} /> {ct.num_empleados ?? 0} empleado(s)</span>
                          <span className="inline-flex items-center gap-1"><FiCalendar size={10} /> {ct.num_festivos_anio} festivos {new Date().getFullYear()}</span>
                        </div>
                      </div>
                      <div className="flex gap-1 ml-2 flex-shrink-0">
                        <button onClick={e => { e.stopPropagation(); abrirModalCentro(ct) }} className="p-1 text-[var(--ink-4)] hover:text-blue-500 rounded">
                          <FiEdit2 size={14} />
                        </button>
                        <button onClick={e => { e.stopPropagation(); eliminarCentro(ct.id) }} className="p-1 text-[var(--ink-4)] hover:text-red-500 rounded">
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                    {ct.latitud && ct.longitud && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
                        <FiMapPin size={10} /> GPS configurado (radio {ct.radio_metros}m)
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Panel de festivos */}
        <div className="lg:col-span-2">
          {!centroSeleccionado ? (
            <div className="card p-8 flex flex-col items-center justify-center text-[var(--ink-4)] h-64">
              <FiMapPin size={40} className="mb-3 opacity-30" />
              <p>Selecciona un centro de trabajo para gestionar sus festivos</p>
            </div>
          ) : (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-semibold text-[var(--ink)]">{centroSeleccionado.nombre}</h2>
                  <p className="text-xs text-[var(--ink-3)]">
                    {centroSeleccionado.comunidad_autonoma
                      ? COMUNIDADES.find(c => c.codigo === centroSeleccionado.comunidad_autonoma)?.nombre || centroSeleccionado.comunidad_autonoma
                      : 'Sin comunidad autónoma configurada'}
                    {centroSeleccionado.municipio && ` · ${centroSeleccionado.municipio}`}
                  </p>
                  <p className="text-xs text-[var(--ink-4)] mt-0.5 flex items-center gap-1">
                    <FiUsers size={11} />
                    Estos festivos aplican a {centroSeleccionado.num_empleados ?? 0} empleado(s) vía {centroSeleccionado.num_grupos ?? 0} grupo(s)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select value={anioFestivos} onChange={e => { setAnioFestivos(Number(e.target.value)); cargarFestivos(centroSeleccionado.id, Number(e.target.value)) }}
                    className="input text-sm py-1 px-2">
                    {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <button onClick={() => abrirModalFestivo()} className="btn btn-secondary text-sm flex items-center gap-1">
                    <FiPlus size={14} /> Añadir
                  </button>
                </div>
              </div>

              {/* Importar desde API */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <FiDownload className="text-blue-600" size={16} />
                  <span className="text-sm font-medium text-blue-800">Importar festivos automáticamente</span>
                  {!centroSeleccionado.comunidad_autonoma && (
                    <span className="text-xs text-orange-600 flex items-center gap-1"><FiInfo size={12} /> Sin C.A.: solo se importarán nacionales</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select value={anioImportar} onChange={e => setAnioImportar(Number(e.target.value))} className="input text-sm py-1 px-2">
                    {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <button onClick={importarFestivos} disabled={importando} className="btn btn-primary text-sm flex items-center gap-1">
                    {importando ? <FiRefreshCw size={14} className="animate-spin" /> : <FiDownload size={14} />}
                    {importando ? 'Importando...' : 'Importar festivos'}
                  </button>
                </div>
              </div>

              {/* Lista de festivos */}
              {loadingFestivos ? (
                <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-10 bg-[var(--bg-2)] rounded animate-pulse" />)}</div>
              ) : festivos.length === 0 ? (
                <div className="text-center py-8 text-[var(--ink-4)]">
                  <FiCalendar size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No hay festivos para {anioFestivos}</p>
                  <p className="text-xs mt-1">Usa la importación automática o añade festivos manualmente</p>
                </div>
              ) : (
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[var(--ink-3)] border-b">
                      <th className="pb-2 font-medium">Fecha</th>
                      <th className="pb-2 font-medium">Nombre</th>
                      <th className="pb-2 font-medium">Tipo</th>
                      <th className="pb-2"></th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {festivos.map(f => (
                        <tr key={f.id} className="hover:bg-[var(--bg-2)]">
                          <td className="py-2 font-mono text-[var(--ink-2)]">{formatearFecha(f.fecha)}</td>
                          <td className="py-2 text-[var(--ink)]">{f.nombre}</td>
                          <td className="py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_FESTIVO_COLOR[f.tipo] || 'bg-[var(--bg-2)] text-[var(--ink-2)]'}`}>
                              {TIPO_FESTIVO_LABEL[f.tipo] || f.tipo}
                            </span>
                          </td>
                          <td className="py-2 text-right">
                            <button onClick={() => abrirModalFestivo(f)} className="p-1 text-[var(--ink-4)] hover:text-blue-500 mr-1"><FiEdit2 size={13} /></button>
                            <button onClick={() => eliminarFestivo(f.id)} className="p-1 text-[var(--ink-4)] hover:text-red-500"><FiTrash2 size={13} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal centro de trabajo */}
      {modalCentro && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold">{centroEditar ? 'Editar centro' : 'Nuevo centro de trabajo'}</h3>
              <button onClick={() => setModalCentro(false)} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX size={20} /></button>
            </div>
            <form onSubmit={guardarCentro} className="p-5 space-y-4">
              <div>
                <label className="label">Nombre *</label>
                <input required value={formCentro.nombre} onChange={e => setFormCentro(p => ({...p, nombre: e.target.value}))} className="input w-full" placeholder="Ej: Oficina Madrid" />
              </div>
              <div>
                <label className="label">Dirección</label>
                <input value={formCentro.direccion} onChange={e => setFormCentro(p => ({...p, direccion: e.target.value}))} className="input w-full" placeholder="Calle, número, ciudad" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">País</label>
                  <input value={formCentro.pais} onChange={e => setFormCentro(p => ({...p, pais: e.target.value}))} className="input w-full" placeholder="ES" maxLength={2} />
                </div>
                <div>
                  <label className="label">Comunidad Autónoma</label>
                  <select value={formCentro.comunidad_autonoma} onChange={e => setFormCentro(p => ({...p, comunidad_autonoma: e.target.value}))} className="input w-full">
                    <option value="">— Seleccionar (opcional) —</option>
                    {COMUNIDADES.map(c => <option key={c.codigo} value={c.codigo}>{c.nombre}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label">Municipio</label>
                <input value={formCentro.municipio} onChange={e => setFormCentro(p => ({...p, municipio: e.target.value}))} className="input w-full" placeholder="Nombre del municipio (para festivos locales)" />
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-[var(--ink-2)] mb-3 flex items-center gap-1"><FiMapPin size={14} /> Geolocalización (opcional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Latitud</label>
                    <input type="number" step="any" value={formCentro.latitud} onChange={e => setFormCentro(p => ({...p, latitud: e.target.value}))} className="input w-full" placeholder="40.4168" />
                  </div>
                  <div>
                    <label className="label">Longitud</label>
                    <input type="number" step="any" value={formCentro.longitud} onChange={e => setFormCentro(p => ({...p, longitud: e.target.value}))} className="input w-full" placeholder="-3.7038" />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="label">Radio de control (metros)</label>
                  <input type="number" min="50" max="5000" value={formCentro.radio_metros} onChange={e => setFormCentro(p => ({...p, radio_metros: e.target.value}))} className="input w-full" />
                  <p className="text-xs text-[var(--ink-4)] mt-1">Distancia máxima permitida para fichar desde este centro</p>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalCentro(false)} className="btn btn-secondary flex-1">Cancelar</button>
                <button type="submit" className="btn btn-primary flex-1">{centroEditar ? 'Guardar cambios' : 'Crear centro'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal festivo manual */}
      {modalFestivo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold">{festivoEditar ? 'Editar festivo' : 'Añadir festivo'}</h3>
              <button onClick={() => setModalFestivo(false)} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX size={20} /></button>
            </div>
            <form onSubmit={guardarFestivo} className="p-5 space-y-4">
              <div>
                <label className="label">Fecha *</label>
                <input required type="date" value={formFestivo.fecha} onChange={e => setFormFestivo(p => ({...p, fecha: e.target.value}))} className="input w-full" />
              </div>
              <div>
                <label className="label">Nombre *</label>
                <input required value={formFestivo.nombre} onChange={e => setFormFestivo(p => ({...p, nombre: e.target.value}))} className="input w-full" placeholder="Ej: Fiesta del trabajo" />
              </div>
              <div>
                <label className="label">Tipo</label>
                <select value={formFestivo.tipo} onChange={e => setFormFestivo(p => ({...p, tipo: e.target.value}))} className="input w-full">
                  <option value="nacional">Nacional</option>
                  <option value="autonomico">Autonómico</option>
                  <option value="local">Local</option>
                  <option value="empresa">Empresa</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalFestivo(false)} className="btn btn-secondary flex-1">Cancelar</button>
                <button type="submit" className="btn btn-primary flex-1">{festivoEditar ? 'Guardar' : 'Añadir'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
